// Login activation API — /onboarding/login-activation* (public).
import { ApiError, get, post } from './client';
import { REAL_API_ENABLED } from './flags';
import { getAccessToken, setTokens } from './session';

const notAvailable = (): never => {
  throw new ApiError('NOT_AVAILABLE', 'Login activation is not available.');
};

export interface LoginActivationInfo {
  token: string;
  activationLink: string;
  customerId: string;
  fullName: string;
  maskedEmail: string;
  maskedMobile: string;
  expiresAt: string;
  status: string;
}

export interface LoginActivationCredentials {
  email: string;
  password: string;
}

export const getLoginActivation = (token: string): Promise<LoginActivationInfo> =>
  REAL_API_ENABLED
    ? get<LoginActivationInfo>(`/onboarding/login-activation/${encodeURIComponent(token)}`)
    : notAvailable();

export const activateLogin = (
  token: string,
  credentials: LoginActivationCredentials,
): Promise<Record<string, unknown>> =>
  REAL_API_ENABLED
    ? post<Record<string, unknown>>(
        `/onboarding/login-activation/${encodeURIComponent(token)}`,
        credentials,
      )
    : notAvailable();

export const resendLoginActivation = (customerId: string): Promise<LoginActivationInfo> =>
  REAL_API_ENABLED
    ? post<LoginActivationInfo>('/onboarding/login-activation/resend', undefined, {
        params: { customerId },
      })
    : notAvailable();


// Dev/test dummy bank verification: the applicant home "approves" the
// application locally instead of calling the employee-only review endpoints.
// The record mirrors the real approve response (account number, IFSC, branch)
// so the dashboard can show the savings account without a backend account.
// A real ACTIVE status from the backend always wins over this marker.
export interface MockVerifiedAccount {
  reference: string;
  customerId: string;
  fullName: string;
  accountType: string;
  accountNumber: string;
  ifsc: string;
  branchName: string;
  activatedAt: string;
  maskedMobile?: string;
  maskedEmail?: string;
}

export type MockVerifiedInput = Pick<
  MockVerifiedAccount,
  'reference' | 'customerId' | 'fullName' | 'accountType'
> &
  Partial<Pick<MockVerifiedAccount, 'maskedMobile' | 'maskedEmail'>>;

const MOCK_VERIFIED_PREFIX = 'nova_mock_verified_';
const MOCK_VERIFIED_LATEST = 'nova_mock_verified_latest';

// Deterministic 12-digit account number from the application reference.
const mockAccountNumberFor = (reference: string): string => {
  let hash = 0;
  for (const ch of reference) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return `1000000${String(hash % 100000).padStart(5, '0')}`;
};

export const markMockVerified = (input: MockVerifiedInput): void => {
  if (!input.reference) return;
  const record: MockVerifiedAccount = {
    ...input,
    accountType: input.accountType || 'SAVINGS',
    accountNumber: mockAccountNumberFor(input.reference),
    ifsc: 'HDFC0000001',
    branchName: 'MG Road Branch',
    activatedAt: new Date().toISOString(),
  };
  localStorage.setItem(`${MOCK_VERIFIED_PREFIX}${input.reference}`, JSON.stringify(record));
  localStorage.setItem(MOCK_VERIFIED_LATEST, input.reference);
};

export const getMockVerifiedAccount = (reference: string): MockVerifiedAccount | null => {
  if (!reference) return null;
  const raw = localStorage.getItem(`${MOCK_VERIFIED_PREFIX}${reference}`);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as MockVerifiedAccount;
    // Ignore legacy markers that stored a bare flag instead of a record.
    return parsed && typeof parsed === 'object' && typeof parsed.reference === 'string'
      ? parsed
      : null;
  } catch {
    return null;
  }
};

export const isMockVerified = (reference: string): boolean =>
  getMockVerifiedAccount(reference) !== null;

/** The most recently mock-approved account — what the dashboard shows in dev. */
export const getLatestMockVerifiedAccount = (): MockVerifiedAccount | null =>
  getMockVerifiedAccount(localStorage.getItem(MOCK_VERIFIED_LATEST) ?? '');

// Dev mock session: lets the applicant "Continue" to the dashboard straight
// after the dummy approval, without a real login. The sentinel token passes
// the ProtectedRoute check; accountApi serves the mock profile/account for it.
export const MOCK_SESSION_TOKEN = 'nova-mock-dev-token';

export const startMockSession = (account: MockVerifiedAccount): void => {
  setTokens(MOCK_SESSION_TOKEN);
  localStorage.setItem('customerId', account.customerId);
};

export const isMockSession = (): boolean =>
  getAccessToken() === MOCK_SESSION_TOKEN;
